import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Skeleton } from "@/components/ui/skeleton";
import { 
  Brain, TrendingUp, RefreshCw, Lightbulb, Target, 
  MessageSquare, DollarSign, Users, Sparkles, ArrowRight
} from "lucide-react";
import { useAILearningLoop } from "@/hooks/useAILearningLoop";
import { cn } from "@/lib/utils";

export function AILearningDashboard() {
  const { 
    learningMetrics, 
    metricsLoading, 
    suggestions, 
    suggestionsLoading,
    retrainModel,
    applyOptimization,
  } = useAILearningLoop();

  const metricCards = [
    { 
      key: 'scoring_accuracy', 
      label: 'Scoring Accuracy', 
      icon: Target, 
      color: 'text-blue-500',
      bgColor: 'bg-blue-500/10',
    },
    { 
      key: 'outreach_effectiveness', 
      label: 'Outreach Effectiveness', 
      icon: MessageSquare, 
      color: 'text-green-500',
      bgColor: 'bg-green-500/10',
    },
    { 
      key: 'objection_success_rate', 
      label: 'Objection Success', 
      icon: Brain, 
      color: 'text-purple-500',
      bgColor: 'bg-purple-500/10',
    },
    { 
      key: 'pricing_win_rate', 
      label: 'Pricing Win Rate', 
      icon: DollarSign, 
      color: 'text-amber-500',
      bgColor: 'bg-amber-500/10',
    },
    { 
      key: 'icp_match_rate', 
      label: 'ICP Match Rate', 
      icon: Users, 
      color: 'text-cyan-500',
      bgColor: 'bg-cyan-500/10',
    },
  ];

  const getMetricValue = (key: string): number => {
    if (!learningMetrics) return 0;
    return learningMetrics[key as keyof typeof learningMetrics] || 0;
  };

  const getImpactBadge = (impact: string) => {
    switch (impact) {
      case 'high':
        return <Badge variant="destructive">High Impact</Badge>;
      case 'medium':
        return <Badge variant="default">Medium Impact</Badge>;
      default:
        return <Badge variant="secondary">Low Impact</Badge>;
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-primary/10">
            <Brain className="h-6 w-6 text-primary" />
          </div>
          <div>
            <h2 className="text-xl font-semibold">AI Learning Loop</h2>
            <p className="text-sm text-muted-foreground">
              Continuous optimization of AI models based on outcomes
            </p>
          </div>
        </div>
        <Button variant="outline" size="sm" onClick={() => retrainModel.mutate('scoring')}>
          <RefreshCw className={cn("h-4 w-4 mr-2", retrainModel.isPending && "animate-spin")} />
          Retrain All Models
        </Button>
      </div>

      {/* Metrics Grid */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        {metricCards.map(metric => {
          const Icon = metric.icon;
          const value = getMetricValue(metric.key);
          return (
            <Card key={metric.key}>
              <CardContent className="p-4">
                {metricsLoading ? (
                  <Skeleton className="h-20 w-full" />
                ) : (
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <div className={cn("p-2 rounded-lg", metric.bgColor)}>
                        <Icon className={cn("h-4 w-4", metric.color)} />
                      </div>
                      <span className={cn(
                        "text-2xl font-bold",
                        value >= 70 ? "text-green-500" : value >= 50 ? "text-amber-500" : "text-red-500"
                      )}>
                        {value}%
                      </span>
                    </div>
                    <div>
                      <div className="text-sm font-medium">{metric.label}</div>
                      <Progress 
                        value={value} 
                        className="h-1.5 mt-2"
                      />
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Optimization Suggestions */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Lightbulb className="h-5 w-5 text-amber-500" />
            AI Optimization Suggestions
          </CardTitle>
          <CardDescription>
            Recommendations to improve AI model performance
          </CardDescription>
        </CardHeader>
        <CardContent>
          {suggestionsLoading ? (
            <div className="space-y-3">
              {[1, 2, 3].map(i => (
                <Skeleton key={i} className="h-20 w-full" />
              ))}
            </div>
          ) : suggestions && suggestions.length > 0 ? (
            <div className="space-y-3">
              {suggestions.map((suggestion, index) => (
                <div 
                  key={index}
                  className="flex items-center justify-between p-4 rounded-lg border bg-card hover:bg-muted/50 transition-colors"
                >
                  <div className="flex items-center gap-4">
                    <div className={cn(
                      "p-2 rounded-lg",
                      suggestion.impact === 'high' ? "bg-red-500/10" :
                      suggestion.impact === 'medium' ? "bg-amber-500/10" : "bg-green-500/10"
                    )}>
                      <TrendingUp className={cn(
                        "h-5 w-5",
                        suggestion.impact === 'high' ? "text-red-500" :
                        suggestion.impact === 'medium' ? "text-amber-500" : "text-green-500"
                      )} />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{suggestion.title}</span>
                        {getImpactBadge(suggestion.impact)}
                      </div>
                      <p className="text-sm text-muted-foreground mt-1">
                        {suggestion.description}
                      </p>
                    </div>
                  </div>
                  <Button 
                    size="sm"
                    onClick={() => applyOptimization.mutate(suggestion)}
                    disabled={applyOptimization.isPending}
                  >
                    <Sparkles className="h-4 w-4 mr-2" />
                    Apply
                    <ArrowRight className="h-4 w-4 ml-2" />
                  </Button>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              <Brain className="h-12 w-12 mx-auto mb-3 opacity-50" />
              <p>All AI models are performing optimally</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
